/**
 * Calgary "Current Year Property Assessments (Parcel)" — public Socrata API (open data).
 * Not a substitute for official assessment records; see City links in UI.
 */

import { computeDetachedLotBand, resolveAssessmentComparableCohort } from "../clientPortal/assessmentComparableCohort";

export const CALGARY_ASSESSMENT_RESOURCE = "https://data.calgary.ca/resource/4bsw-nn7w.json";
/** Historical parcel assessments by roll (multi-year). */
export const CALGARY_HISTORICAL_ASSESSMENT_RESOURCE = "https://data.calgary.ca/resource/4ur7-wsgc.json";
export const CALGARY_SUBPROPERTY_CODES_DATASET = "https://data.calgary.ca/Help-and-Information/Assessment-Subproperty-Use-Codes/5843-8tyj";

export const CALGARY_OPEN_DATA_LINKS = {
  dataset: "https://data.calgary.ca/Government/Current-Year-Property-Assessments-Parcel-/4bsw-nn7w",
  propertyAssessment: "https://www.calgary.ca/property-tax/property-assessment.html",
  openCalgary: "https://opendatacalgary.ca/",
};

function safeCommCode(code) {
  const s = String(code ?? "").trim().toUpperCase();
  return /^[A-Z0-9]{2,6}$/.test(s) ? s : null;
}

/** Calgary open-data assessment_class: RE | NR | FL */
export function normalizeAssessmentClass(raw) {
  const u = String(raw ?? "").trim().toUpperCase();
  if (u === "NR" || u === "RE" || u === "FL") return u;
  return "RE";
}

export function haversineKm(lon1, lat1, lon2, lat2) {
  const R = 6371;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(lat2 - lat1);
  const dLon = toR(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Exterior ring centroid (lng, lat). */
export function multipolygonCentroidLngLat(multipolygon) {
  try {
    const ring = multipolygon?.coordinates?.[0]?.[0];
    if (!Array.isArray(ring) || ring.length < 3) return null;
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const pt of ring) {
      if (!Array.isArray(pt) || pt.length < 2) continue;
      sx += Number(pt[0]);
      sy += Number(pt[1]);
      n++;
    }
    if (!n) return null;
    return [sx / n, sy / n];
  } catch {
    return null;
  }
}

/**
 * When intersects() returns multiple parcels (boundaries), pick the one whose centroid is closest to the geocode point.
 */
export function pickBestAssessmentParcel(rows, lng, lat) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (rows.length === 1) return rows[0];
  let best = rows[0];
  let bestD = Infinity;
  for (const row of rows) {
    const c = multipolygonCentroidLngLat(row.multipolygon);
    if (!c) continue;
    const d = haversineKm(lng, lat, c[0], c[1]);
    if (d < bestD) {
      bestD = d;
      best = row;
    }
  }
  return best;
}

export async function fetchParcelsIntersectingPoint(lng, lat) {
  const lon = Number(lng);
  const la = Number(lat);
  if (!Number.isFinite(lon) || !Number.isFinite(la)) {
    return { ok: false, error: "Invalid coordinates", rows: [] };
  }
  const where = `intersects(multipolygon,'POINT (${lon} ${la})')`;
  const url = `${CALGARY_ASSESSMENT_RESOURCE}?${new URLSearchParams({ $where: where, $limit: "25" })}`;
  const res = await fetch(url);
  if (!res.ok) {
    return { ok: false, error: `Open data request failed (${res.status})`, rows: [] };
  }
  const data = await res.json();
  if (data?.error) {
    return { ok: false, error: data.message || "Open data error", rows: [] };
  }
  const rows = Array.isArray(data) ? data : [];
  return { ok: true, rows };
}

/**
 * Normalize a user-typed civic line toward City assessment `address` strings (e.g. AVENUE → AV).
 * See open data samples: "818 3130 66 AV SW", "15 DEERMEADE PL SE".
 *
 * Important: the dataset uses **AV**, not **AVE** — users often type "66 AVE SW"; we map AVE → AV after AVENUE.
 */
export function normalizeCalgaryAssessmentStreetLine(streetLine) {
  let s = String(streetLine ?? "")
    .trim()
    .toUpperCase()
    .replace(/\u00a0/g, " ")
    .replace(/\./g, "");
  if (!s) return "";

  const abbrevs = [
    [/\bAVENUE\b/g, "AV"],
    [/\bSTREET\b/g, "ST"],
    [/\bDRIVE\b/g, "DR"],
    [/\bROAD\b/g, "RD"],
    [/\bBOULEVARD\b/g, "BV"],
    [/\bLANE\b/g, "LN"],
    [/\bPLACE\b/g, "PL"],
    [/\bCRESCENT\b/g, "CR"],
    [/\bCOURT\b/g, "CO"],
    [/\bGREEN\b/g, "GR"],
    [/\bWAY\b/g, "WY"],
    [/\bTERRACE\b/g, "TC"],
    [/\bTRAIL\b/g, "TR"],
  ];
  for (const [re, rep] of abbrevs) {
    s = s.replace(re, rep);
  }
  // Dataset uses "AV"; users type "AVE" or "Ave" (already uppercased).
  s = s.replace(/\bAVE\b/g, "AV");
  s = s.replace(/\sAVE(\s|$)/g, " AV$1");
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

/** Strip # / "Unit" prefixes so "818" matches City roll "818 3130 …". */
export function normalizeUnitForAssessmentParcelLookup(unit) {
  let u = String(unit ?? "").trim();
  if (!u) return "";
  u = u.toUpperCase().replace(/\s+/g, " ");
  u = u.replace(/^#+\s*/, "");
  u = u.replace(/^(UNIT|SUITE|STE|APT|NO\.?)\s+/i, "");
  return u.trim();
}

/**
 * Full lookup key for `upper(address) = …` on the parcel dataset (unit + street when strata unit is known).
 */
export function buildCalgaryAssessmentAddressLookupKey(unit, streetLine) {
  const st = normalizeCalgaryAssessmentStreetLine(streetLine);
  if (!st) return null;
  const u = normalizeUnitForAssessmentParcelLookup(unit);
  if (u) return `${u} ${st}`.trim();
  return st;
}

/**
 * Fetch a single parcel row by exact assessment address (after normalization).
 * Strata / condo units are stored as civic lines like "818 3130 66 AV SW"; geocoding often misses the building civic.
 */
export async function fetchAssessmentParcelByAddressKey(upperAddressKey) {
  const key = String(upperAddressKey ?? "").trim();
  if (!key) {
    return { ok: false, error: "Missing address", row: null };
  }
  const esc = key.replace(/'/g, "''");
  const where = `upper(address) = '${esc}'`;
  const url = `${CALGARY_ASSESSMENT_RESOURCE}?${new URLSearchParams({ $where: where, $limit: "5" })}`;
  const res = await fetch(url);
  if (!res.ok) {
    return { ok: false, error: `Open data request failed (${res.status})`, row: null };
  }
  const data = await res.json();
  if (data?.error) {
    return { ok: false, error: data.message || "Open data error", row: null };
  }
  const rows = Array.isArray(data) ? data : [];
  if (rows.length === 0) {
    return { ok: true, row: null };
  }
  if (rows.length > 1) {
    return { ok: true, row: rows[0], ambiguous: true };
  }
  return { ok: true, row: rows[0] };
}

/**
 * Exact match on normalized key, then SoQL `like` fallback (spacing / minor spelling).
 */
export async function fetchAssessmentParcelByAddressBestEffort(unit, streetLine) {
  const key = buildCalgaryAssessmentAddressLookupKey(unit, streetLine);
  if (!key) {
    return { ok: true, row: null };
  }
  const first = await fetchAssessmentParcelByAddressKey(key);
  if (!first.ok) return first;
  if (first.row) return first;

  const u = normalizeUnitForAssessmentParcelLookup(unit);
  const st = normalizeCalgaryAssessmentStreetLine(streetLine);
  if (!u || !st) {
    return { ok: true, row: null };
  }

  const stParts = st.split(/\s+/).filter(Boolean);
  const pattern = [u, ...stParts].join("%").replace(/'/g, "''");
  const where = `upper(address) like '${pattern}%'`;
  const url = `${CALGARY_ASSESSMENT_RESOURCE}?${new URLSearchParams({ $where: where, $limit: "15" })}`;
  const res = await fetch(url);
  if (!res.ok) {
    return { ok: false, error: `Open data request failed (${res.status})`, row: null };
  }
  const data = await res.json();
  if (data?.error) {
    return { ok: false, error: data.message || "Open data error", row: null };
  }
  const rows = Array.isArray(data) ? data : [];
  const exactNorm = (a) => String(a ?? "").toUpperCase().replace(/\s+/g, " ").trim();
  const hit =
    rows.find((r) => exactNorm(r.address) === key) ||
    rows.find((r) => {
      const a = exactNorm(r.address);
      return a.startsWith(`${u} `) && stParts.every((t) => a.includes(t));
    }) ||
    rows[0];
  if (!hit) {
    return { ok: true, row: null };
  }
  return { ok: true, row: hit };
}

/** Residential (RE) parcel counts and average assessed value by community — assessment open data. */
export async function fetchResidentialCommunityRollups() {
  const query =
    "SELECT comm_code, comm_name, count(*) AS parcel_count, avg(assessed_value) AS avg_assessed WHERE assessment_class = 'RE' GROUP BY comm_code, comm_name ORDER BY comm_name";
  const url = `${CALGARY_ASSESSMENT_RESOURCE}?${new URLSearchParams({ $query: query })}`;
  const res = await fetch(url);
  if (!res.ok) {
    return { ok: false, error: `Open data request failed (${res.status})`, rows: [] };
  }
  const data = await res.json();
  if (data?.error) {
    return { ok: false, error: data.message || "Open data error", rows: [] };
  }
  return { ok: true, rows: Array.isArray(data) ? data : [] };
}

/**
 * Community roll stats for residential class, optionally restricted to comparable sub_property_use codes
 * (mass-appraisal style strata — not all RE mixed together).
 * @param {string|null} commCode
 * @param {string[]|null} subPropertyUses — if null/empty, all assessment_class RE rows in the community (legacy / broad).
 * @param {{ minSf: number, maxSf: number }|null} landBand — optional lot size filter (land_size_sf); numeric compare works in SoQL on this dataset.
 * @param {string} [assessmentClass] — RE | NR | FL (must match subject parcel for apples-to-apples roll context).
 */
export async function fetchCommunityRollStats(commCode, subPropertyUses, landBand = null, assessmentClass = "RE") {
  const code = safeCommCode(commCode);
  if (!code) {
    return { ok: false, error: "Invalid community code", row: null };
  }
  const ass = normalizeAssessmentClass(assessmentClass);
  let where = `comm_code = '${code}' AND assessment_class = '${ass}'`;
  if (Array.isArray(subPropertyUses) && subPropertyUses.length > 0) {
    const esc = [...new Set(subPropertyUses.map((s) => String(s).replace(/'/g, "").trim()).filter(Boolean))];
    if (!esc.length) {
      return { ok: false, error: "No comparable roll codes", row: null };
    }
    const inList = esc.map((s) => `'${s}'`).join(", ");
    where += ` AND sub_property_use IN (${inList})`;
  }
  if (landBand && Number.isFinite(landBand.minSf) && Number.isFinite(landBand.maxSf)) {
    const lo = Number(landBand.minSf);
    const hi = Number(landBand.maxSf);
    where += ` AND land_size_sf >= ${lo} AND land_size_sf <= ${hi}`;
  }
  const query = `SELECT comm_code, comm_name, count(*) AS parcel_count, avg(assessed_value) AS avg_assessed, min(assessed_value) AS min_assessed, max(assessed_value) AS max_assessed WHERE ${where} GROUP BY comm_code, comm_name`;
  const url = `${CALGARY_ASSESSMENT_RESOURCE}?${new URLSearchParams({ $query: query })}`;
  const res = await fetch(url);
  if (!res.ok) {
    return { ok: false, error: `Open data request failed (${res.status})`, row: null };
  }
  const data = await res.json();
  if (data?.error) {
    return { ok: false, error: data.message || "Open data error", row: null };
  }
  const row = Array.isArray(data) && data[0] ? data[0] : null;
  return { ok: true, row };
}

/** @deprecated Prefer fetchResidentialCommunityDetailForParcel — this mixes all RE dwelling types. */
export async function fetchResidentialCommunityDetail(commCode) {
  const res = await fetchCommunityRollStats(commCode, null);
  return { ...res, cohort: null };
}

/**
 * Comparable community roll stats based on the subject parcel’s sub_property_use / property_type.
 * For **detached** cohorts with lot size on the parcel, also fetches a **lot-size peer band** (optional second row).
 */
export async function fetchResidentialCommunityDetailForParcel(commCode, parcel) {
  const cohort = resolveAssessmentComparableCohort(parcel || {});
  const ass = normalizeAssessmentClass(parcel?.assessment_class);
  const res = await fetchCommunityRollStats(commCode, cohort.subPropertyUses, null, ass);

  let landBandRow = null;
  let landBand = null;
  let landBandNote = null;

  if (ass === "RE" && cohort.id === "detached" && res.ok && res.row) {
    const sf = Number(parcel?.land_size_sf);
    if (Number.isFinite(sf) && sf > 0) {
      let band = computeDetachedLotBand(sf, false);
      let lr = band ? await fetchCommunityRollStats(commCode, cohort.subPropertyUses, band, ass) : null;
      let n = lr?.ok ? Number(lr.row?.parcel_count) : 0;
      if (lr?.ok && lr.row && n > 0 && n < 10) {
        landBandNote = "Lot-size band widened once for more peers.";
        band = computeDetachedLotBand(sf, true);
        lr = band ? await fetchCommunityRollStats(commCode, cohort.subPropertyUses, band, ass) : null;
        n = lr?.ok ? Number(lr.row?.parcel_count) : 0;
      }
      if (lr?.ok && lr.row && n >= 5 && band) {
        landBandRow = lr.row;
        landBand = band;
      } else if (lr?.ok && lr.row && n > 0 && n < 5) {
        landBandNote =
          landBandNote ||
          "Not enough peer parcels in a similar lot-size band to show a separate row — cohort stats above still apply.";
      }
    }
  }

  return { ...res, cohort, landBandRow, landBand, landBandNote, rollAssessmentClass: ass };
}

export function parseAssessedNumber(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * Calgary historical roll queries may use padded (080002702) or unpadded (80002702) roll_number values.
 * @param {string} rollNumber
 * @returns {string[]}
 */
export function buildCalgaryRollLookupVariants(rollNumber) {
  const digits = String(rollNumber ?? "")
    .replace(/\D/g, "")
    .trim();
  if (!digits) return [];
  const variants = new Set();
  variants.add(digits);
  const trimmed = digits.replace(/^0+/, "") || digits;
  variants.add(trimmed);
  if (trimmed.length < 9) {
    variants.add(trimmed.padStart(9, "0"));
  }
  return Array.from(variants);
}

function mergeHistoricalAssessmentRows(rowsA, rowsB) {
  const byYear = new Map();
  const ingest = (rows) => {
    for (const raw of Array.isArray(rows) ? rows : []) {
      if (!raw || typeof raw !== "object") continue;
      const year = parseAssessedNumber(raw.roll_year);
      if (year == null) continue;
      const y = Math.round(year);
      const assessedValue = parseAssessedNumber(raw.assessed_value ?? raw.re_assessed_value);
      const existing = byYear.get(y);
      if (!existing) {
        byYear.set(y, raw);
        continue;
      }
      const existingVal = parseAssessedNumber(existing.assessed_value ?? existing.re_assessed_value);
      if (existingVal == null && assessedValue != null) {
        byYear.set(y, raw);
      }
    }
  };
  ingest(rowsA);
  ingest(rowsB);
  return Array.from(byYear.values()).sort((a, b) => Number(b.roll_year) - Number(a.roll_year));
}

/**
 * Historical assessment rows for one roll number (newest years first in API response).
 * @param {string} rollNumber
 * @param {{ limit?: number }} [options]
 */
export async function fetchHistoricalAssessmentByRollNumber(rollNumber, { limit = 25 } = {}) {
  const variants = buildCalgaryRollLookupVariants(rollNumber);
  if (!variants.length) {
    return { ok: false, error: "Missing roll number", rows: [] };
  }

  const cap = Math.max(1, Math.min(limit, 25));
  let merged = [];
  let lastError = "";

  for (const roll of variants) {
    const esc = roll.replace(/'/g, "''");
    const url = `${CALGARY_HISTORICAL_ASSESSMENT_RESOURCE}?${new URLSearchParams({
      $where: `roll_number='${esc}'`,
      $order: "roll_year DESC",
      $limit: String(cap),
    })}`;
    try {
      const res = await fetch(url);
      if (!res.ok) {
        lastError = `Open data request failed (${res.status})`;
        continue;
      }
      const data = await res.json();
      if (data?.error) {
        lastError = data.message || "Open data error";
        continue;
      }
      const rows = Array.isArray(data) ? data : [];
      if (!rows.length) continue;
      merged = mergeHistoricalAssessmentRows(merged, rows);
    } catch (e) {
      lastError = e?.message || "Could not load assessment history";
    }
  }

  if (!merged.length) {
    return { ok: false, error: lastError || "No historical assessment rows found", rows: [] };
  }

  merged.sort((a, b) => Number(b.roll_year) - Number(a.roll_year));
  return { ok: true, rows: merged.slice(0, cap) };
}

/**
 * Firestore forbids nested arrays. Calgary parcel rows include multipolygon with deep coordinate arrays.
 * Strips that and other non-primitive top-level values before persisting myPropertyParcelSnapshot on the user doc.
 */
export function sanitizeAssessmentParcelForFirestore(row) {
  if (!row || typeof row !== "object") return {};
  const { multipolygon: _mp, ...rest } = row;
  const out = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) continue;
    if (v !== null && typeof v === "object") {
      if (typeof v.toDate === "function") out[k] = v;
      else continue;
    } else {
      out[k] = v;
    }
  }
  return out;
}
